import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import { apiErrorText } from 'helper/apiErrorText';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

// As the api takes them (settings/calls/update, rules): a whole number of
// unanswered calls from 1 to 10, and a window of 'HH:MM' 24 h hours, its
// start before its end (the same strings compare as the hours do).
const MAX_NO_ANSWER = 10;
const isThreshold = (value) =>
  /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= MAX_NO_ANSWER;
const isHour = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
const draftOf = (rules) => ({
  noAnswerThreshold: String(rules.noAnswerThreshold),
  start: rules.callWindow.start,
  end: rules.callWindow.end,
});

/**
 * « Paramètres · Appels » of the admin console: after how many unanswered
 * calls a prospect gets a visit of its own, and the hours in which a call or
 * a callback can be set. Read and saved by the api (admin/crm/settings/calls,
 * `rules` only: the daily goals are saved by « Taille des clients »).
 */
const AdminCallRules = () => {
  const { text, language } = useText('adminConsole');
  const { call, ready } = useCrmContext();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    call(crmApi.getCallSettings).then(
      ({ rules }) => {
        setSaved(rules);
        setDraft(draftOf(rules));
      },
      () => setFailed(true),
    );
  }, [call]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const changed =
    saved &&
    draft &&
    Object.entries(draftOf(saved)).some(([key, value]) => draft[key] !== value);
  let problem = null;
  if (draft && !isThreshold(draft.noAnswerThreshold))
    problem = text('rulesInvalidThreshold');
  else if (draft && !(isHour(draft.start) && isHour(draft.end)))
    problem = text('rulesInvalidHour');
  else if (draft && draft.start >= draft.end) problem = text('rulesOrder');

  const save = () => {
    setSaving(true);
    call(crmApi.saveCallSettings, {
      rules: {
        noAnswerThreshold: Number(draft.noAnswerThreshold),
        callWindow: { start: draft.start, end: draft.end },
      },
    }).then(
      // The values shown are the api's, as it kept them.
      ({ rules }) => {
        setSaving(false);
        setSaved(rules);
        setDraft(draftOf(rules));
        toast.success(text('rulesSaved'));
      },
      (error) => {
        setSaving(false);
        toast.error(apiErrorText(error, { language }));
      },
    );
  };

  const edit = (key) => (event) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));
  const inputClass = (valid, width) =>
    `h-9 ${width} rounded-md border bg-white px-3 text-right text-sm text-gray-900 focus:outline-none ${valid ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`;
  const windowValid =
    draft && isHour(draft.start) && isHour(draft.end) && draft.start < draft.end;

  return (
    <PageForm title={text('rulesPageTitle')}>
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 py-6 lg:px-8">
        {failed && (
          <CompactEmptyState
            title={text('rulesLoadError')}
            action={{ label: text('goalsRetry'), onClick: load }}
          />
        )}
        {!failed && !draft && (
          <div
            className="h-40 animate-pulse rounded-lg bg-gray-100"
            aria-busy="true"
          />
        )}
        {!failed && draft && (
          <section
            aria-labelledby="call-rules-title"
            className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white"
          >
            <div className="flex flex-col gap-0.5 px-4 py-4">
              <h2
                id="call-rules-title"
                className="text-base font-semibold text-gray-900"
              >
                {text('rulesTitle')}
              </h2>
              <p className="text-sm text-gray-500">{text('rulesText')}</p>
            </div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-y border-gray-50">
                  <td className="py-3 pl-4 pr-2 text-gray-900">
                    {text('rulesNoAnswer')}
                  </td>
                  <td className="flex items-center justify-end gap-2 py-3 pl-2 pr-4 text-gray-600">
                    {text('rulesNoAnswerBefore')}
                    <input
                      type="text"
                      inputMode="numeric"
                      aria-label={text('rulesNoAnswerLabel')}
                      value={draft.noAnswerThreshold}
                      onChange={edit('noAnswerThreshold')}
                      className={inputClass(
                        isThreshold(draft.noAnswerThreshold),
                        'w-20',
                      )}
                    />
                    {text('rulesNoAnswerAfter')}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 pl-4 pr-2 text-gray-900">
                    {text('rulesWindow')}
                  </td>
                  <td className="flex items-center justify-end gap-2 py-3 pl-2 pr-4 text-gray-600">
                    {text('rulesWindowFrom')}
                    <input
                      type="time"
                      aria-label={text('rulesWindowStartLabel')}
                      value={draft.start}
                      onChange={edit('start')}
                      className={inputClass(windowValid, 'w-28')}
                    />
                    {text('rulesWindowTo')}
                    <input
                      type="time"
                      aria-label={text('rulesWindowEndLabel')}
                      value={draft.end}
                      onChange={edit('end')}
                      className={inputClass(windowValid, 'w-28')}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              {text('rulesHow')}
            </p>
            {changed && (
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-4 py-3">
                <span className="mr-auto text-xs text-gray-500">
                  {problem ?? text('goalsUnsaved')}
                </span>
                <Button
                  color="SECONDARY"
                  size="SM"
                  onClick={() => setDraft(draftOf(saved))}
                >
                  {text('goalsCancel')}
                </Button>
                <Button
                  color="PRIMARY"
                  size="SM"
                  disabled={Boolean(problem) || saving}
                  onClick={save}
                >
                  {text('goalsSave')}
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
    </PageForm>
  );
};

export default AdminCallRules;
